import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAffiliateCheck } from "@/hooks/useAffiliateCheck";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Handshake, RefreshCw, Loader2, LayoutDashboard, BookOpen, Users,
  DollarSign, PlusCircle, UserCircle, HelpCircle, LogOut, Menu, X,
  ChevronRight, ShieldOff, TrendingUp,
} from "lucide-react";
import { AffiliateOverviewTab } from "@/components/affiliate/AffiliateOverviewTab";
import { AffiliateBookingsTab } from "@/components/affiliate/AffiliateBookingsTab";
import { AffiliateClientsTab } from "@/components/affiliate/AffiliateClientsTab";
import { AffiliateCommissionsTab } from "@/components/affiliate/AffiliateCommissionsTab";
import { AffiliateProfileTab } from "@/components/affiliate/AffiliateProfileTab";
import { AffiliateSupportTab } from "@/components/affiliate/AffiliateSupportTab";
import { AffiliateCampaignCreate } from "@/components/affiliate/AffiliateCampaignCreate";

// ── Types ──
interface Referral {
  id: string; referral_code: string; client_email: string | null;
  sale_amount: number; commission_rate: number; commission_amount: number;
  status: string; created_at: string; paid_at: string | null;
}
interface Client {
  id: string; client_name: string; client_email: string | null;
  client_company: string | null; client_phone: string | null;
  notes: string | null; created_at: string;
}
interface Booking {
  id: string; campaign_id: string; campaign_name: string | null;
  brand_name: string | null; company_name: string | null; email: string | null;
  total_budget: number | null; start_date: string; end_date: string;
  status: string; created_at: string;
}

type TabKey = "overview" | "bookings" | "clients" | "commissions" | "create" | "profile" | "support";

// ── Count-up hook (matches media owner) ──
function useCountUp(target: number, duration = 1200, startDelay = 0) {
  const [count, setCount] = useState(0);
  const prevTarget = useRef(0);
  useEffect(() => {
    if (target === 0) { setCount(0); return; }
    const start = prevTarget.current;
    const diff = target - start;
    const timer = setTimeout(() => {
      const startTime = performance.now();
      const tick = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(start + diff * ease));
        if (progress < 1) requestAnimationFrame(tick);
        else prevTarget.current = target;
      };
      requestAnimationFrame(tick);
    }, startDelay);
    return () => clearTimeout(timer);
  }, [target, duration, startDelay]);
  return count;
}

// ── Stat Card (matches media owner) ──
interface StatCardProps {
  label: string; rawValue: number | string; countValue?: number;
  prefix?: string; suffix?: string; icon: React.ElementType;
  gradient: string; iconColor: string; delay: string;
}
const StatCard = ({ label, rawValue, countValue, prefix = '', suffix = '', icon: Icon, gradient, iconColor, delay }: StatCardProps) => {
  const displayValue = countValue !== undefined ? `${prefix}${countValue.toLocaleString()}${suffix}` : rawValue;
  return (
    <div className="relative rounded-2xl border border-border/60 bg-card overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 animate-fade-in" style={{ animationDelay: delay }}>
      <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${gradient}`} />
      <div className="absolute -right-3 -top-3 opacity-[0.06]"><Icon className={`h-20 w-20 ${iconColor}`} /></div>
      <div className="p-5 relative z-10">
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} inline-flex mb-3`}><Icon className="h-5 w-5 text-white" /></div>
        <p className="text-2xl font-bold text-foreground tabular-nums tracking-tight leading-none mb-1.5">{displayValue}</p>
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
        <div className={`mt-4 h-px bg-gradient-to-r ${gradient} opacity-20`} />
      </div>
    </div>
  );
};

const AffiliateDashboard = () => {
  const { language } = useLanguage();
  const { user, signOut } = useAuth();
  const { isAffiliate, checking } = useAffiliateCheck();
  const navigate = useNavigate();
  const isRTL = language === 'ar';

  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [application, setApplication] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState("");
  const [clientInfo, setClientInfo] = useState({ clientName: "", clientEmail: "", clientCompany: "", clientPhone: "" });

  useEffect(() => {
    if (!checking && !isAffiliate) navigate('/affiliate-signup');
  }, [isAffiliate, checking, navigate]);

  const fetchData = useCallback(async () => {
    if (!user || !isAffiliate) return;
    setLoading(true);
    try {
      setReferralCode(`AFF-${user.id.substring(0, 8).toUpperCase()}`);
      const [referralsRes, clientsRes, bookingsRes, appRes] = await Promise.all([
        supabase.from('affiliate_referrals').select('*').eq('affiliate_user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('affiliate_clients').select('*').eq('affiliate_user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('saved_campaigns').select('id, campaign_id, campaign_name, brand_name, company_name, email, total_budget, start_date, end_date, status, created_at').eq('affiliate_user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('affiliate_applications').select('*').eq('user_id', user.id).maybeSingle(),
      ]);
      if (referralsRes.data) setReferrals(referralsRes.data as Referral[]);
      if (clientsRes.data) setClients(clientsRes.data as Client[]);
      if (bookingsRes.data) setBookings(bookingsRes.data as Booking[]);
      if (appRes.data) setApplication(appRes.data);
    } catch (error) {
      console.error('Error fetching affiliate data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, isAffiliate]);

  useEffect(() => { if (isAffiliate) fetchData(); }, [isAffiliate, fetchData]);

  const handleLogout = useCallback(async () => { await signOut(); navigate('/'); }, [signOut, navigate]);
  const switchTab = useCallback((tab: TabKey) => { setActiveTab(tab); setSidebarOpen(false); }, []);

  // Stats
  const totalSales = referrals.reduce((sum, r) => sum + r.sale_amount, 0);
  const totalCommission = referrals.reduce((sum, r) => sum + r.commission_amount, 0);
  const pendingCommission = referrals.filter(r => r.status === 'pending' || r.status === 'confirmed').reduce((sum, r) => sum + r.commission_amount, 0);
  const paidCommission = referrals.filter(r => r.status === 'paid').reduce((sum, r) => sum + r.commission_amount, 0);

  const animSales = useCountUp(totalSales, 1200, 0);
  const animCommission = useCountUp(totalCommission, 1200, 80);
  const animClients = useCountUp(clients.length, 1200, 160);
  const animBookings = useCountUp(bookings.length, 1200, 240);

  const navItems = useMemo(() => [
    { key: "overview" as TabKey, label: "Overview", labelAr: "نظرة عامة", icon: LayoutDashboard },
    { key: "bookings" as TabKey, label: "Bookings", labelAr: "حجوزاتي", icon: BookOpen },
    { key: "clients" as TabKey, label: "Clients", labelAr: "العملاء", icon: Users },
    { key: "commissions" as TabKey, label: "Commissions", labelAr: "العمولات", icon: DollarSign },
    { key: "create" as TabKey, label: "Create Campaign", labelAr: "إنشاء حملة", icon: PlusCircle },
    { key: "profile" as TabKey, label: "Profile", labelAr: "الملف", icon: UserCircle },
    { key: "support" as TabKey, label: "Support", labelAr: "الدعم", icon: HelpCircle },
  ], []);

  // Loading state (media owner style)
  if (checking || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary" />
            <div className="absolute inset-0 rounded-full animate-ping border-2 border-primary/20" style={{ animationDuration: '1.5s' }} />
          </div>
          <p className="text-muted-foreground font-medium">{isRTL ? 'جاري التحقق...' : 'Verifying access...'}</p>
        </div>
      </div>
    );
  }

  if (!isAffiliate) return null;

  const userInitials = (user?.email?.[0] || 'A').toUpperCase();

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'text-right' : 'text-left'}`}>
      {/* ── HERO HEADER ── */}
      <div data-tour="aff-header" className="relative overflow-hidden border-b border-border/50"
        style={{ background: 'linear-gradient(135deg, hsl(var(--primary)/0.12) 0%, hsl(var(--primary)/0.04) 50%, transparent 100%)' }}>
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, hsl(var(--primary)), transparent)' }} />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full opacity-5" style={{ background: 'hsl(var(--primary))' }} />

        <div className="container mx-auto px-4 sm:px-6 py-6 relative z-10">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden p-2 rounded-xl bg-card border border-border shadow-sm hover:bg-accent transition-colors" onClick={() => setSidebarOpen(true)}>
                <Menu className="h-5 w-5 text-foreground" />
              </button>
              <div className="p-3 bg-primary/10 rounded-2xl border border-primary/20">
                <Handshake className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight tracking-tight">{isRTL ? 'لوحة تحكم الشريك' : 'Affiliate Dashboard'}</h1>
                <p className="text-sm text-muted-foreground">{isRTL ? 'إدارة الإحالات والعمولات' : 'Manage your referrals and commissions'}</p>
                {user?.email && (
                  <p className="text-xs text-muted-foreground/70 mt-0.5">
                    {isRTL ? `مرحباً بعودتك 👋` : `Welcome back 👋`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-border shadow-sm">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">{userInitials}</div>
                <span className="text-xs font-medium text-foreground">{isRTL ? 'شريك' : 'Partner'}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => fetchData()} className="rounded-xl border-border/70">
                <RefreshCw className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">{isRTL ? 'تحديث' : 'Refresh'}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} className="rounded-xl border-border/70 hover:border-destructive/50 hover:text-destructive transition-all">
                <LogOut className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">{isRTL ? 'خروج' : 'Logout'}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="container mx-auto px-4 sm:px-6 py-5">
        <div data-tour="aff-stats" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label={isRTL ? 'إجمالي المبيعات' : 'Total Sales'} rawValue={totalSales} countValue={animSales} suffix=" SAR" icon={DollarSign} gradient="from-blue-500 to-blue-600" iconColor="text-blue-500" delay="0ms" />
          <StatCard label={isRTL ? 'إجمالي العمولات' : 'Total Commission'} rawValue={totalCommission} countValue={animCommission} suffix=" SAR" icon={TrendingUp} gradient="from-emerald-500 to-emerald-600" iconColor="text-emerald-500" delay="80ms" />
          <StatCard label={isRTL ? 'العملاء النشطون' : 'Active Clients'} rawValue={clients.length} countValue={animClients} icon={Users} gradient="from-violet-500 to-violet-600" iconColor="text-violet-500" delay="160ms" />
          <StatCard label={isRTL ? 'إجمالي الحجوزات' : 'Total Bookings'} rawValue={bookings.length} countValue={animBookings} icon={BookOpen} gradient="from-amber-500 to-orange-500" iconColor="text-amber-500" delay="240ms" />
        </div>
      </div>

      {/* ── LAYOUT: Sidebar + Content ── */}
      <div className="container mx-auto px-4 sm:px-6 pb-10">
        <div className="flex gap-6 items-start">
          {/* Desktop Sidebar */}
          <nav data-tour="aff-nav" className="hidden lg:flex flex-col w-56 flex-shrink-0 sticky top-6">
            <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
              <div className="p-2 space-y-0.5">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.key;
                  return (
                    <button key={item.key} onClick={() => switchTab(item.key)}
                      data-tour={item.key === 'create' ? 'aff-create' : item.key === 'clients' ? 'aff-clients' : item.key === 'commissions' ? 'aff-commissions' : undefined}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group relative
                        ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>
                      {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-foreground/60 rounded-r-full" />}
                      <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-accent-foreground'}`} />
                      <span className="flex-1 text-left">{isRTL ? item.labelAr : item.label}</span>
                      {isActive && <ChevronRight className="h-3.5 w-3.5 text-primary-foreground/60" />}
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-border/50 p-2 mt-1">
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all">
                  <LogOut className="h-4 w-4" /><span>{isRTL ? 'تسجيل الخروج' : 'Sign Out'}</span>
                </button>
              </div>
            </div>
          </nav>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex">
              <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
              <div className="relative w-64 bg-card border-r border-border shadow-2xl flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <span className="font-semibold text-foreground">{isRTL ? 'القائمة' : 'Navigation'}</span>
                  <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-accent"><X className="h-4 w-4" /></button>
                </div>
                <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.key;
                    return (
                      <button key={item.key} onClick={() => switchTab(item.key)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                          ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 text-left">{isRTL ? item.labelAr : item.label}</span>
                      </button>
                    );
                  })}
                </nav>
                <div className="border-t border-border p-2">
                  <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all">
                    <LogOut className="h-4 w-4" /><span>{isRTL ? 'تسجيل الخروج' : 'Sign Out'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Content Area */}
          <main className="flex-1 min-w-0 animate-fade-in" key={activeTab}>
            {activeTab === 'overview' && (
              <AffiliateOverviewTab totalSales={totalSales} totalCommission={totalCommission} pendingCommission={pendingCommission} paidCommission={paidCommission} activeClients={clients.length} totalBookings={bookings.length} referrals={referrals} bookings={bookings} onTabChange={(tab) => switchTab(tab as TabKey)} />
            )}
            {activeTab === 'bookings' && <AffiliateBookingsTab bookings={bookings} />}
            {activeTab === 'clients' && <AffiliateClientsTab clients={clients} bookings={bookings} onRefresh={fetchData} />}
            {activeTab === 'commissions' && <AffiliateCommissionsTab referrals={referrals} />}
            {activeTab === 'create' && <AffiliateCampaignCreate clientInfo={clientInfo} onClientInfoChange={setClientInfo} />}
            {activeTab === 'profile' && <AffiliateProfileTab application={application} />}
            {activeTab === 'support' && <AffiliateSupportTab />}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
